import {
  type InboxPage as InboxPageData,
  type InboxTab,
  PARTICIPATION_STATES,
  QUESTION_STATUSES,
} from '@fruuit/shared'
import {
  Button,
  Callout,
  Checkbox,
  EmptyState,
  PageHeader,
  Pagination,
  Select,
  SkeletonText,
  Tabs,
} from '@fruuit/ui'
import { Inbox, MessagesSquare, UsersRound } from 'lucide-react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { useApiQuery } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { StaffClientActionList } from '../../features/client-actions/ClientActionsPanel'
import { keys } from '../../features/engagements/queries'
import { useCursorPager } from '../../features/engagements/useCursorPager'
import { ParticipationCard } from '../../features/participation/ParticipationCard'
import { QuestionThread } from '../../features/questions/QuestionThread'
import { useTitle } from '../../lib/useTitle'

const TABS: { id: InboxTab; label: string }[] = [
  { id: 'questions', label: 'Questions' },
  { id: 'participation', label: 'Participation' },
  { id: 'client_actions', label: 'Client actions' },
]

const STATUS_OPTIONS: Record<InboxTab, { value: string; label: string }[]> = {
  questions: QUESTION_STATUSES.values.map((v) => ({
    value: v,
    label: QUESTION_STATUSES.labels[v],
  })),
  participation: [
    { value: 'open', label: 'Open (needs a response)' },
    ...PARTICIPATION_STATES.values.map((v) => ({
      value: v,
      label: PARTICIPATION_STATES.labels[v],
    })),
  ],
  client_actions: [],
}

/**
 * The staff cross-org inbox (I-08, D-18): investor questions, participation requests and
 * client actions across clients. Client actions are read-only (R-12). Every load is audited
 * on the core chain and on each client's chain.
 */
export function InboxPage() {
  useTitle('Inbox', 'Workspace')
  const me = useSession()
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((t) => t.id === params.get('tab'))?.id ?? 'questions') as InboxTab
  const org = params.get('org') ?? ''
  const statusParam = params.get('status')
  const status =
    statusParam === 'all' ? '' : (statusParam ?? (tab === 'participation' ? 'open' : ''))
  const mine = params.get('mine') === 'true'
  const pager = useCursorPager(`${tab}|${org}|${status}|${mine}`)

  const query: Record<string, string | number> = { tab, limit: 20 }
  if (org) query.org_id = org
  if (status && tab !== 'client_actions') query.status = status
  if (mine && tab !== 'client_actions') query.mine = 'true'
  if (pager.cursor) query.cursor = pager.cursor
  const inbox = useApiQuery<InboxPageData>([...keys.inbox, query], '/inbox', {
    query,
    placeholderData: (prev) => prev,
  })

  function set(next: Record<string, string | null>) {
    const out = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value) out.set(key, value)
      else out.delete(key)
    }
    setParams(out, { replace: true })
  }

  const counts = inbox.data?.counts
  const countFor = (id: InboxTab) =>
    counts
      ? id === 'questions'
        ? counts.questions_awaiting_reply
        : id === 'participation'
          ? counts.participation_open
          : counts.client_actions
      : undefined

  const filters = (
    <div className="mb-5 grid gap-3 sm:grid-cols-[minmax(12rem,1fr)_minmax(12rem,1fr)_auto] sm:items-end">
      <Select
        label="Client"
        placeholder="All clients"
        options={(inbox.data?.orgs ?? []).map((o) => ({ value: o.id, label: o.name }))}
        value={org}
        onChange={(event) => set({ org: event.target.value || null })}
      />
      {tab !== 'client_actions' ? (
        <Select
          label="Status"
          placeholder="Any status"
          options={STATUS_OPTIONS[tab]}
          value={status}
          onChange={(event) =>
            set({ status: event.target.value || (tab === 'participation' ? 'all' : null) })
          }
        />
      ) : (
        <div />
      )}
      {tab !== 'client_actions' ? (
        <Checkbox
          label="Assigned to me"
          checked={mine}
          onChange={(event) => set({ mine: event.target.checked ? 'true' : null })}
          className="sm:pb-2.5"
        />
      ) : null}
    </div>
  )

  let body: ReactNode
  if (inbox.isPending) body = <SkeletonText lines={8} />
  else if (inbox.isError)
    body = (
      <Callout tone="danger" role="alert">
        {inbox.error.message}
      </Callout>
    )
  else {
    const page = inbox.data
    if (page.items.length === 0) {
      body = (
        <EmptyState
          icon={
            page.tab === 'questions' ? (
              <MessagesSquare />
            ) : page.tab === 'participation' ? (
              <UsersRound />
            ) : (
              <Inbox />
            )
          }
          title="Nothing matches these filters."
          description="Try another client or status, or clear “Assigned to me”."
          action={
            <Button
              variant="secondary"
              onClick={() => set({ org: null, status: null, mine: null })}
            >
              Clear filters
            </Button>
          }
        />
      )
    } else if (page.tab === 'questions') {
      body = (
        <div className="grid gap-4 xl:grid-cols-2">
          {page.items.map((q) => (
            <QuestionThread
              key={q.id}
              variant="staff"
              question={q}
              currentUserId={me.user.id}
              showEngagement
            />
          ))}
        </div>
      )
    } else if (page.tab === 'participation') {
      body = (
        <div className="grid gap-4 xl:grid-cols-2">
          {page.items.map((r) => (
            <ParticipationCard
              key={r.id}
              variant="staff"
              request={r}
              casePath={`/workspace/engagements/${r.engagement.id}`}
              managers={page.managers}
            />
          ))}
        </div>
      )
    } else {
      body = <StaffClientActionList items={page.items} />
    }
  }

  const content = (
    <>
      {/* The cards use h3 titles; this keeps the heading outline h1 → h2 → h3 (Q-03). */}
      <h2 className="sr-only">{TABS.find((t) => t.id === tab)?.label}</h2>
      {filters}
      {body}
      {inbox.data && (inbox.data.next_cursor || pager.hasPrevious) ? (
        <Pagination
          className="mt-5"
          hasPrevious={pager.hasPrevious}
          hasNext={Boolean(inbox.data.next_cursor)}
          onPrevious={pager.previous}
          onNext={() => pager.next(inbox.data?.next_cursor)}
          summary={`Page ${pager.page}`}
          busy={inbox.isFetching}
        />
      ) : null}
    </>
  )

  return (
    <>
      <PageHeader
        eyebrow="Client inbox"
        title="What clients are asking."
        description="Questions, participation requests and client actions across every client. Replies and responses stay in Fruuit Engage; nothing is emailed."
      />
      <Tabs
        label="Inbox sections"
        value={tab}
        onValueChange={(next) =>
          setParams(next === 'questions' ? {} : { tab: next }, { replace: true })
        }
        items={TABS.map((t) => ({
          id: t.id,
          label: t.label,
          ...(countFor(t.id) !== undefined && t.id !== 'client_actions'
            ? { count: countFor(t.id) }
            : {}),
          content: t.id === tab ? content : null,
        }))}
      />
      <p className="mt-6 text-xs text-sage">
        Each view of this inbox is recorded in the audit trail for the clients it shows.
      </p>
    </>
  )
}
